import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ThermometerIcon, ZapIcon, WindIcon, LightbulbIcon, ToggleLeftIcon, ChevronRightIcon, RadarIcon, PersonStandingIcon, DropletsIcon } from 'lucide-react';
type SensorType = 'SEO' | 'SEL' | 'SES' | 'RADAR';
interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
  x: number;
  y: number;
}
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface SensorListViewProps {
  room: Room;
  sensors: Sensor[];
  onSelectSensor: (sensor: Sensor) => void;
}
// ─── Scoring helpers ───
function scoreTemp(temp: number): number {
  if (temp >= 22 && temp <= 26) return 100;
  if (temp >= 20 && temp < 22 || temp > 26 && temp <= 28) return 70;
  if (temp >= 18 && temp < 20 || temp > 28 && temp <= 30) return 40;
  return 10;
}
function scoreHumidity(h: number): number {
  if (h >= 40 && h <= 60) return 100;
  if (h >= 30 && h < 40 || h > 60 && h <= 70) return 70;
  if (h >= 20 && h < 30 || h > 70 && h <= 80) return 40;
  return 10;
}
function scoreCO2(ppm: number): number {
  if (ppm < 800) return 100;
  if (ppm < 1200) return 70;
  if (ppm < 2000) return 40;
  return 10;
}
function scoreTVOC(mgm3: number): number {
  if (mgm3 < 0.3) return 100;
  if (mgm3 < 0.6) return 70;
  if (mgm3 < 1.0) return 40;
  return 10;
}
function scorePM25(ugm3: number): number {
  if (ugm3 < 15) return 100;
  if (ugm3 < 35) return 70;
  if (ugm3 < 75) return 40;
  return 10;
}
function getAQIGrade(score: number) {
  if (score >= 80) return {
    label: '좋음',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20'
  };
  if (score >= 60) return {
    label: '보통',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20'
  };
  if (score >= 40) return {
    label: '나쁨',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20'
  };
  return {
    label: '매우 나쁨',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20'
  };
}
const ROOM_AIR_QUALITY: Record<string, {
  co2: number;
  tvoc: number;
  pm25: number;
}> = {
  living: {
    co2: 450,
    tvoc: 0.18,
    pm25: 12
  },
  bedroom: {
    co2: 900,
    tvoc: 0.45,
    pm25: 22
  },
  bedroom2: {
    co2: 520,
    tvoc: 0.22,
    pm25: 18
  },
  master_bedroom: {
    co2: 680,
    tvoc: 0.35,
    pm25: 28
  },
  kitchen: {
    co2: 1100,
    tvoc: 0.72,
    pm25: 42
  },
  bathroom: {
    co2: 380,
    tvoc: 0.15,
    pm25: 8
  },
  bathroom2: {
    co2: 400,
    tvoc: 0.12,
    pm25: 10
  },
  dressroom: {
    co2: 550,
    tvoc: 0.28,
    pm25: 15
  },
  outdoor_unit: {
    co2: 420,
    tvoc: 0.1,
    pm25: 35
  },
  entrance: {
    co2: 480,
    tvoc: 0.2,
    pm25: 20
  },
  alpha_room: {
    co2: 620,
    tvoc: 0.32,
    pm25: 16
  }
};
const ROOM_HUMIDITY: Record<string, number> = {
  living: 48,
  bedroom: 62,
  bedroom2: 45,
  master_bedroom: 55,
  kitchen: 72,
  bathroom: 85,
  bathroom2: 78,
  dressroom: 50,
  outdoor_unit: 40,
  entrance: 44,
  alpha_room: 47
};
// Mock energy data per room
const ROOM_ENERGY_STATUS: Record<string, {
  comfort: string;
  power: string;
}> = {
  living: {
    comfort: '쾌적함',
    power: '12.4 kWh'
  },
  bedroom: {
    comfort: '쾌적성 저하',
    power: '5.8 kWh'
  },
  bedroom2: {
    comfort: '쾌적함',
    power: '4.3 kWh'
  },
  master_bedroom: {
    comfort: '보통',
    power: '6.7 kWh'
  },
  kitchen: {
    comfort: '보통',
    power: '8.2 kWh'
  },
  bathroom: {
    comfort: '쾌적함',
    power: '2.1 kWh'
  },
  bathroom2: {
    comfort: '쾌적함',
    power: '1.8 kWh'
  },
  dressroom: {
    comfort: '쾌적함',
    power: '1.2 kWh'
  },
  outdoor_unit: {
    comfort: '보통',
    power: '3.5 kWh'
  },
  entrance: {
    comfort: '쾌적함',
    power: '0.8 kWh'
  },
  alpha_room: {
    comfort: '쾌적함',
    power: '2.9 kWh'
  }
};
// Mock sensor values
const MOCK_SENSOR_VALUES: Record<string, {
  power?: number;
  temp?: number;
  humidity?: number;
  present?: boolean;
}> = {
  'seo-living-1': {
    power: 18.2
  },
  'sel-living-1': {
    power: 12
  },
  'ses-living-1': {
    temp: 24.5,
    humidity: 48
  },
  'seo-bedroom-1': {
    power: 8.4
  },
  'sel-bedroom-1': {
    power: 6.5
  },
  'ses-bedroom-1': {
    temp: 27.1,
    humidity: 62
  },
  'radar-bedroom-1': {
    present: true
  },
  'sel-bedroom2-1': {
    power: 4.2
  },
  'seo-master-1': {
    power: 15.3
  },
  'seo-kitchen-1': {
    power: 22.1
  },
  'sel-kitchen-1': {
    power: 3.8
  },
  'ses-kitchen-1': {
    temp: 22.3,
    humidity: 72
  },
  'seo-bathroom-1': {
    power: 5.6
  },
  'sel-bathroom-1': {
    power: 7.1
  },
  'ses-bathroom2-1': {
    temp: 21.8,
    humidity: 78
  },
  'sel-dressroom-1': {
    power: 4.0
  },
  'seo-outdoor-1': {
    power: 9.7
  },
  'ses-entrance-1': {
    temp: 22.0,
    humidity: 44
  },
  'sel-alpha-1': {
    power: 3.2
  }
};
const getSensorIcon = (type: SensorType) => {
  switch (type) {
    case 'SEO':
      return ZapIcon;
    case 'SEL':
      return LightbulbIcon;
    case 'SES':
      return ToggleLeftIcon;
    case 'RADAR':
      return RadarIcon;
    default:
      return ZapIcon;
  }
};
const getSensorColor = (type: SensorType) => {
  switch (type) {
    case 'SEO':
      return {
        icon: 'text-violet-400',
        bg: 'bg-violet-500/20'
      };
    case 'SEL':
      return {
        icon: 'text-amber-400',
        bg: 'bg-amber-500/20'
      };
    case 'SES':
      return {
        icon: 'text-cyan-400',
        bg: 'bg-cyan-500/20'
      };
    case 'RADAR':
      return {
        icon: 'text-rose-400',
        bg: 'bg-rose-500/20'
      };
    default:
      return {
        icon: 'text-white',
        bg: 'bg-white/20'
      };
  }
};
const getSensorTypeName = (type: SensorType) => {
  switch (type) {
    case 'SEO':
      return 'Smart Outlet';
    case 'SEL':
      return 'Smart Light';
    case 'SES':
      return 'Smart Switch';
    case 'RADAR':
      return 'Radar Sensor';
    default:
      return 'Sensor';
  }
};
function getComfortScoreLabel(temp: number, humidity: number) {
  const ts = scoreTemp(temp);
  const hs = scoreHumidity(humidity);
  const avg = (ts + hs) / 2;
  if (avg >= 80) return {
    label: '쾌적',
    color: 'text-emerald-400'
  };
  if (avg >= 60) return {
    label: '양호',
    color: 'text-cyan-400'
  };
  if (avg >= 40) return {
    label: '보통',
    color: 'text-amber-400'
  };
  return {
    label: '불쾌',
    color: 'text-red-400'
  };
}
export function SensorListView({
  room,
  sensors,
  onSelectSensor
}: SensorListViewProps) {
  const roomHumidity = ROOM_HUMIDITY[room.id] || 48;
  const comfortInfo = getComfortScoreLabel(room.temp, roomHumidity);
  const energyStatus = ROOM_ENERGY_STATUS[room.id] || {
    comfort: '쾌적함',
    power: '0 kWh'
  };
  const airData = ROOM_AIR_QUALITY[room.id] || {
    co2: 400,
    tvoc: 0.15,
    pm25: 10
  };
  const co2Score = scoreCO2(airData.co2);
  const tvocScore = scoreTVOC(airData.tvoc);
  const pm25Score = scorePM25(airData.pm25);
  const overallAQI = Math.round((co2Score + tvocScore + pm25Score) / 3);
  const aqiGrade = getAQIGrade(overallAQI);
  return <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-3" data-id="element-798">
      {/* Summary Cards Row */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} className="grid grid-cols-3 gap-2.5" data-id="element-799">
        {/* 쾌적성 */}
        <div className="glass-panel rounded-xl p-3 flex flex-col items-center gap-1.5" data-id="element-800">
          <div className="p-1.5 rounded-lg bg-cyan-500/20" data-id="element-801">
            <ThermometerIcon className="w-4 h-4 text-cyan-400" data-id="element-802" />
          </div>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold" data-id="element-803">
            쾌적성
          </span>
          <span className={`text-xs font-bold ${comfortInfo.color}`} data-id="element-804">
            {comfortInfo.label}
          </span>
          <div className="flex items-center gap-1 text-[10px] text-slate-500 font-mono" data-id="element-805">
            <span data-id="element-806">{room.temp}°C</span>
            <span data-id="element-807">·</span>
            <span data-id="element-808">{roomHumidity}%</span>
          </div>
        </div>

        {/* 전기사용량 */}
        <div className="glass-panel rounded-xl p-3 flex flex-col items-center gap-1.5" data-id="element-809">
          <div className="p-1.5 rounded-lg bg-amber-500/20" data-id="element-810">
            <ZapIcon className="w-4 h-4 text-amber-400" data-id="element-811" />
          </div>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold" data-id="element-812">
            전기사용량
          </span>
          <span className="text-xs font-bold text-amber-400 font-mono" data-id="element-813">
            {energyStatus.power}
          </span>
        </div>

        {/* 종합공기질 */}
        <div className="glass-panel rounded-xl p-3 flex flex-col items-center gap-1.5" data-id="element-814">
          <div className={`p-1.5 rounded-lg ${aqiGrade.bgColor}`} data-id="element-815">
            <WindIcon className={`w-4 h-4 ${aqiGrade.color}`} data-id="element-816" />
          </div>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold" data-id="element-817">
            공기질
          </span>
          <span className={`text-xs font-bold ${aqiGrade.color}`} data-id="element-818">
            {aqiGrade.label}
          </span>
          <span className={`text-[10px] font-mono font-bold ${aqiGrade.color}`} data-id="element-819">
            {overallAQI}점
          </span>
        </div>
      </motion.div>

      {/* Sensor List */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.1
    }} data-id="element-820">
        <div className="flex items-center gap-2 mb-2.5 px-1" data-id="element-821">
          <span className="text-xs text-slate-500 font-bold uppercase tracking-wider" data-id="element-822">
            센서 ({sensors.length}개)
          </span>
        </div>

        <div className="space-y-2" data-id="element-823">
          {sensors.map((sensor, i) => {
          const Icon = getSensorIcon(sensor.type);
          const colors = getSensorColor(sensor.type);
          const values = MOCK_SENSOR_VALUES[sensor.id];
          return <motion.button key={sensor.id} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.15 + i * 0.04
          }} onClick={() => onSelectSensor(sensor)} className="w-full flex items-center gap-3 p-3 rounded-xl glass-panel active:bg-white/[0.06] transition-colors min-h-[56px]" data-id="element-824">
                {/* Icon */}
                <div className={`p-2 rounded-lg ${colors.bg} ${colors.icon} flex-shrink-0`} data-id="element-825">
                  <Icon className="w-4.5 h-4.5" data-id="element-826" />
                </div>

                {/* Label + Type */}
                <div className="flex-1 text-left min-w-0" data-id="element-827">
                  <div className="text-sm font-medium text-slate-200 truncate" data-id="element-828">
                    {sensor.label}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono" data-id="element-829">
                    {getSensorTypeName(sensor.type)}
                  </div>
                </div>

                {/* Values */}
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-830">
                  {sensor.type === 'SEO' && values?.power !== undefined && <span className="text-sm font-mono font-bold text-violet-400" data-id="element-831">
                      {values.power}W
                    </span>}
                  {sensor.type === 'SEL' && values?.power !== undefined && <span className="text-sm font-mono font-bold text-amber-400" data-id="element-832">
                      {values.power}W
                    </span>}
                  {sensor.type === 'SES' && values?.temp !== undefined && <div className="flex items-center gap-1.5 text-xs font-mono" data-id="element-833">
                      <span className="font-bold text-rose-400" data-id="element-834">
                        {values.temp}°C
                      </span>
                      <span className="text-slate-600" data-id="element-835">|</span>
                      <span className="font-bold text-cyan-400 flex items-center gap-0.5" data-id="element-836">
                        <DropletsIcon className="w-3 h-3" data-id="element-837" />
                        {values.humidity}%
                      </span>
                    </div>}
                  {sensor.type === 'RADAR' && <div className={`flex items-center gap-1 text-xs font-bold ${values?.present ? 'text-rose-400' : 'text-slate-600'}`} data-id="element-838">
                      <PersonStandingIcon className="w-4 h-4" data-id="element-839" />
                      <span data-id="element-840">{values?.present ? '재실' : '부재'}</span>
                    </div>}
                  {/* Fallback for sensors without mock data */}
                  {!values && sensor.type !== 'RADAR' && <span className="text-xs font-mono text-slate-600" data-id="element-841">--</span>}
                </div>

                {/* Arrow */}
                <ChevronRightIcon className="w-4 h-4 text-slate-600 flex-shrink-0" data-id="element-842" />
              </motion.button>;
        })}
        </div>

        {sensors.length === 0 && <div className="text-center py-12 text-slate-600 text-sm" data-id="element-843">
            이 영역에 등록된 센서가 없습니다
          </div>}
      </motion.div>
    </div>;
}